import AutomatedScoringType from "enums/AutomatedScoringType";
import strings from "features/qualityForm/formEditor/question/AutomatedScoringSection.strings";
import { DefaultLimits } from "constants/DefaultLimits";

/**
 * Checks the disabling status and corresponding tooltip message of each option in Automated Scoring
 *
 * @param {AutomatedScoringType} selectedOption
 * @param {boolean} hasAiScoringEditPermission
 * @param {boolean} hasEvaluationAssistancePermission
 * @param {boolean} hasMaxAiScoringQuestionsReached
 * @param {boolean} hasMaxAiScoringAnswerOptionsReached
 * @param {typeof strings.labels.tooltip} tooltipStrings
 * @returns an object which tells whether each option is disabled and has corresponding tooltip message
 */
export const getAutomatedScoringOptionState = (
    selectedOption: AutomatedScoringType | undefined,
    hasAiScoringEditPermission: boolean,
    hasEvaluationAssistancePermission: boolean,
    hasMaxAiScoringQuestionsReached: boolean,
    hasMaxAiScoringAnswerOptionsReached: boolean,
    tooltipStrings: typeof strings.labels.tooltip,
    maxAiScoringQuestions: number
) => {
    const optionStates = {};
    for (const type of Object.values(AutomatedScoringType)) {
        optionStates[type] = {
            disabled: false,
            tooltip: "",
        };
    }

    const aiScoringLimitReached =
        hasMaxAiScoringAnswerOptionsReached || hasMaxAiScoringQuestionsReached;

    if (!hasAiScoringEditPermission && !hasEvaluationAssistancePermission) {
        // set each AutomatedScoringType as true
        for (const type of Object.values(AutomatedScoringType)) {
            optionStates[type].disabled = true;
            optionStates[type].tooltip =
                tooltipStrings.noAutomatedScoringPermission;
        }
    } else if (!hasAiScoringEditPermission) {
        if (selectedOption === AutomatedScoringType.AI_SCORING) {
            for (const type of Object.values(AutomatedScoringType)) {
                optionStates[type].disabled = true;
                optionStates[type].tooltip =
                    tooltipStrings.noAutomatedScoringPermission;
            }
        } else {
            optionStates[AutomatedScoringType.AI_SCORING].disabled = true;
            optionStates[AutomatedScoringType.AI_SCORING].tooltip =
                tooltipStrings.noAiScoringEditPermission;
        }
    } else if (!hasEvaluationAssistancePermission) {
        if (selectedOption === AutomatedScoringType.EVALUATION_ASSISTANCE) {
            for (const type of Object.values(AutomatedScoringType)) {
                optionStates[type].disabled = true;
                optionStates[type].tooltip =
                    tooltipStrings.noAutomatedScoringPermission;
            }
        } else if (selectedOption === AutomatedScoringType.AI_SCORING) {
            optionStates[AutomatedScoringType.EVALUATION_ASSISTANCE].disabled =
                true;
            optionStates[AutomatedScoringType.EVALUATION_ASSISTANCE].tooltip =
                tooltipStrings.noEvaluationAssistancePermission;
        } else {
            if (aiScoringLimitReached) {
                for (const type of Object.values(AutomatedScoringType)) {
                    optionStates[type].disabled = true;
                }
                // set No Automation disabled option tooltip message
                optionStates[AutomatedScoringType.NO_AUTOMATION].tooltip =
                    tooltipStrings.noPermissionAndAiScoringLimitReached;

                // set AI Scoring disabled option tooltip message
                if (hasMaxAiScoringAnswerOptionsReached) {
                    optionStates[AutomatedScoringType.AI_SCORING].tooltip +=
                        tooltipStrings.aiScoringAnswerLimitReached({
                            maxAIScoringAnswerOptionCount:
                                DefaultLimits.maxAIScoringAnswerOptionCount,
                        });
                }

                if (hasMaxAiScoringQuestionsReached) {
                    if (optionStates[AutomatedScoringType.AI_SCORING].tooltip) {
                        optionStates[AutomatedScoringType.AI_SCORING].tooltip +=
                            " ";
                    }
                    optionStates[AutomatedScoringType.AI_SCORING].tooltip +=
                        tooltipStrings.aiScoringMaxQuestionsTooltip({
                            maxAIScoringQuestions: maxAiScoringQuestions,
                        });
                }

                // set Evaluation Assistance option tooltip message
                optionStates[
                    AutomatedScoringType.EVALUATION_ASSISTANCE
                ].tooltip = tooltipStrings.noEvaluationAssistancePermission;
            } else {
                optionStates[
                    AutomatedScoringType.EVALUATION_ASSISTANCE
                ].disabled = true;
                optionStates[
                    AutomatedScoringType.EVALUATION_ASSISTANCE
                ].tooltip = tooltipStrings.noEvaluationAssistancePermission;
            }
        }
    } else {
        if (
            aiScoringLimitReached &&
            selectedOption !== AutomatedScoringType.AI_SCORING
        ) {
            optionStates[AutomatedScoringType.AI_SCORING].disabled = true;
            if (hasMaxAiScoringAnswerOptionsReached) {
                optionStates[AutomatedScoringType.AI_SCORING].tooltip +=
                    tooltipStrings.aiScoringAnswerLimitReached({
                        maxAIScoringAnswerOptionCount:
                            DefaultLimits.maxAIScoringAnswerOptionCount,
                    });
            }

            if (hasMaxAiScoringQuestionsReached) {
                if (optionStates[AutomatedScoringType.AI_SCORING].tooltip) {
                    optionStates[AutomatedScoringType.AI_SCORING].tooltip +=
                        " ";
                }
                optionStates[AutomatedScoringType.AI_SCORING].tooltip +=
                    tooltipStrings.aiScoringMaxQuestionsTooltip({
                        maxAIScoringQuestions: maxAiScoringQuestions,
                    });
            }
        }
    }

    return optionStates;
};
